// src/components/ProfilePage.js
import React, { useEffect, useState } from "react";
import "./ProfilePage.css";
import axios from "../axios";
import { toast } from "react-toastify";
import ContactInput from "./ContactInput";
import BackLink from "./BackLink";

const ProfilePage = ({ token, user, setUser }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Состояния для добавления нового номера
  const [showNewPhoneBlock, setShowNewPhoneBlock] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [sending, setSending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [fullName, setFullName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameMessage, setNameMessage] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailStep, setEmailStep] = useState(1); // 1: ввод email, 2: ввод кода
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailChangeError, setEmailChangeError] = useState("");
  const [emailChangeMessage, setEmailChangeMessage] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(0);
  const [showAvatarFull, setShowAvatarFull] = useState(false);

  // Загружаем email + телефоны
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [userRes, phonesRes] = await Promise.all([
          axios.get("/api/user/", { headers }),
          axios.get("/api/phone-numbers/", { headers }),
        ]);

        setUserInfo(userRes.data);
        setPhones(phonesRes.data);
        setFullName(userRes.data?.profile?.name || "");
      } catch (err) {
        console.error("Σφάλμα φόρτωσης προφίλ:", err);
        setError("Σφάλμα φόρτωσης προφίλ.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [token]);

   // Таймер для кнопки "Аποστολή ξανά"
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => {
      setResendTimer((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

    // Таймер для кнопки "Аποστολή ξανά" email
   useEffect(() => {
    if (emailResendTimer <= 0) return;
    const t = setInterval(() => {
      setEmailResendTimer((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [emailResendTimer]);

  // Отправка кода для нового телефона (как в CreateAdForm)
  const sendCode = async () => {
    if (resendTimer > 0 || sending) return;
    setConfirmError("");

    if (!newPhone.trim()) {
      setConfirmError("📱 Πρέπει να εισάγετε αριθμό τηλεφώνου.");
      return;
    }

    try {
      setSending(true);
      await axios.post("/api/send-code/", {
        contact: newPhone,
        password: "temporary",
        mode: "verify",
      });
      toast.info("📨 Ο κωδικός στάλθηκε!");
      setCodeSent(true);
      setResendTimer(60);
    } catch (err) {
      console.error("Σφάλμα αποστολής κωδικού:", err);
      setConfirmError("❌ Σφάλμα αποστολής.");
    } finally {
      setSending(false);
    }
  };

  // Подтверждение нового номера
  const verifyPhoneNumber = async () => {
    if (!code.trim()) {
      setConfirmError("Πρέπει να εισάγετε κωδικό επιβεβαίωσης.");
      return;
    }

    try {
      setSending(true);
      await axios.post("/api/verify-phone/", { code, phone: newPhone });
      toast.success("✅ Ο αριθμός τηλεφώνου επιβεβαιώθηκε!");

      // обновляем список номеров
      const updated = await axios.get("/api/phone-numbers/");
      setPhones(updated.data || []);

      // очищаем форму
      setShowNewPhoneBlock(false);
      setNewPhone("");
      setCode("");
      setCodeSent(false);
      setResendTimer(0);
      setConfirmError("");
    } catch (err) {
      console.error("Σφάλμα επιβεβαίωσης τηλεφώνου:", err);
      setConfirmError(
        err.response?.data?.error || "Σφάλμα επιβεβαίωσης."
      );
    } finally {
      setSending(false);
    }
  };


  const handleSaveName = async () => {
    setNameMessage("");

    if (!fullName.trim()) {
      setNameMessage("Παρακαλώ εισάγετε ονοματεπώνυμο.");
      return;
    }

    try {
      setSavingName(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.patch(
        "/api/update-profile/",
        { name: fullName },
        { headers }
      );

      // обновляем локально состояние на странице
      setUserInfo(data);
      setFullName(data?.profile?.name || "");

      // обновляем глобального пользователя, чтобы шапка сразу показала ФИΟ
      if (setUser) {
        setUser(data);
      }
      setIsEditingName(false); // закрываем режим редактирования

      toast.success("✅ Το ονοματεπώνυμο αποθηκεύτηκε!");
    } catch (err) {
      console.error("Σφάλμα αποθήκευσης ονόματος:", err);
      setNameMessage("❌ Σφάλμα αποθήκευσης.");
    } finally {
      setSavingName(false);
    }
  };

  // открыть форму редактирования имени
  const startNameEdit = () => {
    setIsEditingName(true);
    setFullName(userInfo?.profile?.name || "");
    setNameMessage("");
  };

  // ==== СМЕНА EMAIL ====

  // 1) открыть форму редактирования
  const startEmailEdit = () => {
    setIsEditingEmail(true);
    setEmailStep(1);
    setNewEmail(userInfo?.email || "");
    setEmailCode("");
    setEmailChangeError("");
    setEmailChangeMessage("");
  };

  // 2) отправка кода на новый email
  const handleSendEmailCode = async () => {
    // если ещё идёт отправка или таймер > 0 — ничего не делаем
    if (emailLoading || emailResendTimer > 0) return;

    setEmailChangeError("");
    setEmailChangeMessage("");

    if (!newEmail.trim()) {
      setEmailChangeError("Πρέπει να εισάγετε νέο email.");
      return;
    }

    try {
      setEmailLoading(true);
      await axios.post("/api/send-email-change-code/", {
        new_email: newEmail,
      });

      setEmailChangeMessage("✅ Ο κωδικός στάλθηκε στο νέο email.");
      setEmailStep(2);
      setEmailResendTimer(60); // ⬅️ запускаем обратный отсчёт 60 секунд
    } catch (err) {
      console.error("Σφάλμα αποστολής κωδικού αλλαγής email:", err);
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        "❌ Σφάλμα αποστολής κωδικού. Προσπαθήστε ξανά.";
      setEmailChangeError(msg);
    } finally {
      setEmailLoading(false);
    }
  };

  // 3) подтверждение кода и смена email
  const handleConfirmEmailChange = async () => {
    setEmailChangeError("");
    setEmailChangeMessage("");

    if (!emailCode.trim()) {
      setEmailChangeError("Πρέπει να εισάγετε τον κωδικό.");
      return;
    }

    try {
      setEmailLoading(true);
      const res = await axios.post("/api/confirm-email-change/", {
        new_email: newEmail,
        code: emailCode,
      });

      // backend возвращает { message, user }
      const updatedUser = res.data?.user;
      if (updatedUser) {
        setUserInfo(updatedUser);
        if (setUser) {
          setUser(updatedUser); // чтобы шапка сразу обновила email
        }
      }

      setIsEditingEmail(false);
      setEmailStep(1);
      setEmailCode("");
      setEmailResendTimer(0);
      setEmailChangeMessage("✅ Το email άλλαξε με επιτυχία.");
      setTimeout(() => setEmailChangeMessage(""), 3000);
      toast.success("✅ Το email άλλαξε με επιτυχία.");
    } catch (err) {
      console.error("Σφάλμα επιβεβαίωσης αλλαγής email:", err);
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        "❌ Σφάλμα επιβεβαίωσης. Ελέγξτε τον κωδικό.";
      setEmailChangeError(msg);
    } finally {
      setEmailLoading(false);
    }
  };

  // Удаление номера телефона
  const handleDeletePhone = async (phoneId) => {
    const ok = window.confirm("Θέλεις σίγουρα να διαγράψεις αυτόν τον αριθμό;");
    if (!ok) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`/api/phone-numbers/${phoneId}/`, { headers });

      setPhones((prev) => prev.filter((p) => p.id !== phoneId));
    } catch (err) {
      console.error("Σφάλμα διαγραφής αριθμού:", err);
      alert("Σφάλμα διαγραφής αριθμού.");
    }
  };

  if (!token) {
    return (
      <div className="profile-page">
        <p className="profile-error">
          Πρέπει να συνδεθείς για να δεις το προφίλ σου.
        </p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <BackLink label="Πίσω" />
      {loading ? (
        <div className="profile-loading">Φόρτωση...</div>
      ) : error ? (
        <div className="profile-error">{error}</div>
      ) : (
        <>
          <h1 className="profile-title">Το προφίλ μου</h1>

          {/* 🔹 БЛОК АВАТАРА КАК В ШАПКЕ */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <div
              className="avatar-wrapper"
              onClick={() => {
                if (userInfo?.profile?.avatar) {
                  setShowAvatarFull(true);
                }
              }}
              style={{ cursor: userInfo?.profile?.avatar ? "zoom-in" : "default" }}
            >
              {userInfo?.profile?.avatar ? (
                <img
                  src={userInfo.profile.avatar}
                  alt="avatar"
                  className="avatar"
                />
              ) : (
                <div className="placeholder-avatar">
                  {(userInfo?.profile?.name ||
                    userInfo?.email ||
                    ""
                  )
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
            </div>

            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 600 }}>
                {userInfo?.profile?.name || userInfo?.email || "Χωρίς όνομα"}
              </div>
              {userInfo?.email && (
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  {userInfo.email}
                </div>
              )}
            </div>
          </div>

          {/* ==== EMAIL ==== */}
          <div className="profile-section">
            <div className="profile-section-header">
              <div className="profile-section-title">Email</div>
            </div>

            {/* Строка с текущим email + карандаш */}
            <div className="profile-row">
              <div className="profile-row-left">
                <span className="profile-value">
                  {userInfo?.email || ""}
                </span>

                <button
                  className="edit-btn"
                  type="button"
                  onClick={startEmailEdit}
                  title="Αλλαγή email"
                >
                  ✏️
                </button>
              </div>

              <div className="profile-row-right verified">
                <span className="check-icon">✔</span>
                <span className="verified-text">Επιβεβαιωμένο</span>
              </div>
            </div>

            {/* Блок смены email — появляется только когда нажали ✏️ */}
            {isEditingEmail && (
              <div className="profile-email-edit-block">
                {/* ШАГ 1: ввод нового email и отправка кода */}
                {emailStep === 1 && (
                  <>
                    <p className="profile-hint">
                      Εισάγετε το νέο email. Θα στείλουμε έναν 6-ψήφιο κωδικό επιβεβαίωσης.
                    </p>
                    <input
                      type="email"
                      className="profile-input"
                      placeholder="Νέο email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                    <div className="profile-row-right" style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSendEmailCode}
                        disabled={emailLoading || emailResendTimer > 0}
                      >
                        {emailLoading ? "Αποστολή..." : "Αποστολή κωδικού"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        style={{ marginLeft: 8 }}
                        onClick={() => setIsEditingEmail(false)}
                      >
                        Άκυρο
                      </button>
                    </div>
                  </>
                )}

                {/* ШАГ 2: ввод кода + подтвердить + отправить заново */}
                {emailStep === 2 && (
                  <>
                    <p className="profile-hint">
                      Πληκτρολογήστε τον 6-ψήφιο κωδικό που στάλθηκε στο {newEmail}.
                    </p>
                    <input
                      type="text"
                      className="profile-input"
                      placeholder="6-ψήφιος κωδικός"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                    />
                    <div className="profile-row-right" style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleConfirmEmailChange}
                        disabled={emailLoading}
                      >
                        {emailLoading ? "Επιβεβαίωση..." : "Επιβεβαίωση"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        style={{ marginLeft: 8 }}
                        onClick={handleSendEmailCode}
                        disabled={emailLoading || emailResendTimer > 0}
                        title={
                          emailResendTimer > 0
                            ? `Μπορείτε ξανά σε ${emailResendTimer}s`
                            : "Αποστολή ξανά"
                        }
                      >
                        {emailResendTimer > 0
                          ? `Αποστολή ξανά (${emailResendTimer})`
                          : "Αποστολή ξανά"}
                      </button>
                    </div>
                  </>
                )}

                {emailChangeError && (
                  <p className="error-message" style={{ marginTop: 8 }}>
                    {emailChangeError}
                  </p>
                )}
                {emailChangeMessage && (
                  <p className="profile-hint" style={{ marginTop: 4 }}>
                    {emailChangeMessage}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ΤΗΛΕΦΩΝΑ */}
          <section className="profile-section">
            {/* Заголовок + кнопка "Добавить номер" */}
            <div className="profile-section-header">
              <div className="profile-section-title">Τηλέφωνο</div>

              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setShowNewPhoneBlock((v) => !v)}
              >
                ➕ Προσθήκη αριθμού
              </button>
            </div>

            {/* Блок добавления нового номера */}
            {showNewPhoneBlock && (
              <div className="new-phone-form">
                <label>Νέος αριθμός:</label>
                <ContactInput
                  value={newPhone}
                  onChange={setNewPhone}
                  placeholder="Τηλέφωνο"
                />

                {!codeSent ? (
                  <button
                    type="button"
                    className="btn btn-outline w-100"
                    onClick={sendCode}
                    disabled={sending}
                  >
                    Αποστολή κωδικού
                  </button>
                ) : (
                  <>
                    <input
                      className="input-lg"
                      placeholder="Κωδικός επιβεβαίωσης"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                    <div className="verify-buttons">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={verifyPhoneNumber}
                        disabled={sending}
                      >
                        Επιβεβαίωση
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={sendCode}
                        disabled={resendTimer > 0 || sending}
                        title={
                          resendTimer > 0
                            ? `Μπορείτε ξανά σε ${resendTimer}s`
                            : "Αποστολή ξανά"
                        }
                      >
                        {resendTimer > 0
                          ? `Αποστολή ξανά (${resendTimer})`
                          : "Αποστολή ξανά"}
                      </button>
                    </div>
                  </>
                )}

                {confirmError && (
                  <p className="error-message">{confirmError}</p>
                )}
              </div>
            )}

            {/* Список сохранённых телефонов — как было */}
            {phones.length === 0 ? (
              <div className="profile-empty">
                Δεν έχετε αποθηκευμένους αριθμούς.
              </div>
            ) : (
              phones.map((phone) => (
                <div key={phone.id} className="profile-row">
                  <div className="profile-row-left">
                    <span className="profile-value">{phone.phone}</span>
                  </div>

                  <div className="profile-row-right verified">
                    {phone.is_verified && (
                      <>
                        <span className="check-icon">✔</span>
                        <span className="verified-text">
                          Επιβεβαιωμένο
                        </span>
                      </>
                    )}

                    <button
                      type="button"
                      className="profile-phone-delete"
                      onClick={() => handleDeletePhone(phone.id)}
                    >
                      Διαγραφή
                    </button>
                  </div>
                </div>
              ))
            )}
          </section>

          {/* Ονοματεπώνυμο */}
          <section className="profile-section">
            <div className="profile-section-header">
              <div className="profile-section-title">Ονοματεπώνυμο</div>
            </div>

            {/* Строка с текущим именем + карандаш */}
            <div className="profile-row">
              <div className="profile-row-left" style={{ flex: 1 }}>
                <span className="profile-value">
                  {userInfo?.profile?.name || "—"}
                </span>
                <button
                  type="button"
                  className="edit-btn"
                  onClick={startNameEdit}
                  title="Αλλαγή ονοματεπωνύμου"
                >
                  ✏️
                </button>
              </div>
            </div>

            {/* Блок редактирования имени — только когда нажали ✏️ */}
            {isEditingName && (
              <div className="profile-email-edit-block">
                <p className="profile-hint">
                  Εισάγετε το νέο ονοματεπώνυμο.
                </p>
                <input
                  type="text"
                  className="profile-input"
                  placeholder="Πλήρες όνομα"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
                <div className="profile-row-right" style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveName}
                    disabled={savingName}
                  >
                    {savingName ? "Αποθήκευση..." : "Αποθήκευση"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ marginLeft: 8 }}
                    onClick={() => {
                      setIsEditingName(false);
                      setFullName(userInfo?.profile?.name || "");
                    }}
                  >
                    Άκυρο
                  </button>
                </div>
              </div>
            )}

            {nameMessage && <p className="profile-hint">{nameMessage}</p>}
          </section>
        </>
      )}

      {showAvatarFull && userInfo?.profile?.avatar && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
          onClick={() => setShowAvatarFull(false)}
        >
          <img
            src={userInfo.profile.avatar}
            alt="avatar-full"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProfilePage;